import { useRef, useState, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useManufacturer } from '@/api/hooks'
import { useCreateSourcingOption, type SourcingOption, type Supplier } from '@/api/sourcing'
import { ActionPopover } from '@/components/action-popover'
import { SupplierPicker } from '@/components/supplier-picker'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useSwitchPurchaseOrderSource, useUpdatePurchaseOrder } from '@/features/purchase-orders/hooks'
import { formatApiErrorMessage } from '@/lib/api-error'
import type { PurchaseOrderDetail } from './types'

export type DetailChoiceOption<T extends string> = {
  value: T
  label: string
  /** Marked in the list only, never in the shown value. */
  preferred?: boolean
}

/**
 * A details-grid value shown as plain text that opens a picker on click, like the
 * dashboard's inline edits (po-inline-edit). The list can mark the preferred option.
 */
export function DetailChoice<T extends string>({
  label,
  value,
  display,
  options,
  placeholder,
  disabled,
  onChange,
  onCloseAutoFocus,
}: {
  label: string
  value: T | undefined
  display: ReactNode
  options: ReadonlyArray<DetailChoiceOption<T>>
  placeholder?: string
  disabled?: boolean
  onChange: (value: T) => void
  /** Prevent it when the choice opens a popover, so focus stays there. */
  onCloseAutoFocus?: (event: Event) => void
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as T)} disabled={disabled}>
      <SelectTrigger
        aria-label={label}
        title={`Change ${label.toLowerCase()}`}
        className="-ml-2 h-8 w-auto max-w-full cursor-pointer gap-1 border-transparent bg-transparent px-2 font-medium shadow-none hover:bg-accent focus-visible:border-transparent disabled:hover:bg-transparent data-[placeholder]:font-normal dark:bg-transparent dark:hover:bg-accent [&>svg]:size-3 [&>svg]:opacity-60"
      >
        <SelectValue placeholder={placeholder}>{display}</SelectValue>
      </SelectTrigger>
      <SelectContent position="popper" align="start" onCloseAutoFocus={onCloseAutoFocus}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
            {/* The space keeps "Preferred" a separate word for screen readers. */}
            {option.preferred && ' '}
            {option.preferred && (
              <span className="rounded-sm bg-muted px-1.5 py-px text-[11px] font-medium text-muted-foreground">
                Preferred
              </span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function optionLabel(option: Pick<SourcingOption, 'supplier'> & { product_scope?: string | null }) {
  const name = option.supplier?.name ?? 'Direct'
  return option.product_scope ? `${name} · ${option.product_scope}` : name
}

// Mirrors UNSENT_PO_STATUSES in the backend's purchase_order_cancellation.py: the supplier never got these.
const UNSENT_PO_STATUSES = new Set(['generated', 'pending', 'spec_credit_pending', 'spec_credit_expired', 'po_held'])
// Replaced, cancelled or returned POs stay where they were bought.
const CLOSED_PO_STATUSES = new Set(['replaced', 'cancelled', 'order_cancelled', 'disregard', 'returned'])
const ANOTHER_SUPPLIER = 'another'

/** Details-grid row: which of the manufacturer's sources this PO is bought through. */
export function POSourceControl({ po }: { po: PurchaseOrderDetail }) {
  const manufacturer = useManufacturer(po.manufacturer?.uuid ?? '')
  const updatePO = useUpdatePurchaseOrder(po.uuid)
  // The supplier picked in the row; undefined for Another supplier, which starts with none picked.
  const [switchTo, setSwitchTo] = useState<{ supplier: Supplier | null | undefined } | null>(null)
  const options = manufacturer.data?.sourcing_options ?? []
  const chosen = po.source?.sourcing_option_id
  const chosenOption = options.find((option) => option.id === chosen)
  const loaded = po.source?.sourcing_option
  const current = chosenOption ?? (loaded?.id === chosen ? loaded : undefined)
  const display = current ? optionLabel(current) : undefined
  // The supplier has this PO number, so moving it makes a new PO and this one is Replaced (USA-1689).
  const sent = !UNSENT_PO_STATUSES.has(po.status)
  const opensPopover = useRef(false)

  return (
    <>
      <dt className="pt-1.5 text-xs text-muted-foreground">Source</dt>
      <dd className="min-w-0">
        <SwitchSupplierPopover
          po={po}
          sent={sent}
          currentName={current?.supplier?.name ?? po.manufacturer?.short_name}
          switchTo={switchTo}
          onClose={() => setSwitchTo(null)}
          trigger={
            <div className="min-w-0">
              <DetailChoice
                label="Source"
                value={chosen != null ? String(chosen) : undefined}
                display={display}
                placeholder="Not recorded"
                options={[
                  ...options.map((option) => ({
                    value: String(option.id),
                    label: optionLabel(option),
                    preferred: option.rank === 1,
                  })),
                  { value: ANOTHER_SUPPLIER, label: 'Another supplier…' },
                ]}
                disabled={updatePO.isPending || manufacturer.isLoading || CLOSED_PO_STATUSES.has(po.status)}
                onCloseAutoFocus={(event) => {
                  if (opensPopover.current) event.preventDefault()
                  opensPopover.current = false
                }}
                onChange={(next) => {
                  const option = options.find((candidate) => String(candidate.id) === next)
                  if (next === ANOTHER_SUPPLIER || sent) {
                    opensPopover.current = true
                    setSwitchTo({ supplier: option ? (option.supplier ?? null) : undefined })
                    return
                  }
                  updatePO.mutate(
                    { sourcing_option_id: Number(next) },
                    {
                      onError: (error) =>
                        toast.error(formatApiErrorMessage('Failed to change where this PO is bought', error)),
                    },
                  )
                }}
              />
            </div>
          }
        />
      </dd>
    </>
  )
}

/**
 * Confirm moving the PO to another supplier. A PO the supplier already has gets a new PO at the new
 * supplier and is marked Replaced, and the old supplier gets the cancellation email; an unsent PO
 * just changes source. A supplier not yet among the manufacturer's sources is added as one.
 */
function SwitchSupplierPopover({
  po,
  sent,
  currentName,
  switchTo,
  onClose,
  trigger,
}: {
  po: PurchaseOrderDetail
  sent: boolean
  currentName: string | undefined
  switchTo: { supplier: Supplier | null | undefined } | null
  onClose: () => void
  trigger: ReactNode
}) {
  const navigate = useNavigate()
  const manufacturerUuid = po.manufacturer?.uuid ?? ''
  const manufacturer = useManufacturer(manufacturerUuid)
  const createSource = useCreateSourcingOption(manufacturerUuid)
  const updatePO = useUpdatePurchaseOrder(po.uuid)
  const switchSource = useSwitchPurchaseOrderSource(po.uuid)
  const [picked, setPicked] = useState<{ supplier: Supplier | null } | null>(null)
  const [wasOpen, setWasOpen] = useState(false)
  const open = switchTo !== null
  // Every opening starts from the source picked in the row.
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setPicked(null)
  }
  const chosen = picked ?? (switchTo?.supplier === undefined ? null : { supplier: switchTo.supplier })
  const sources = manufacturer.data?.sourcing_options ?? []
  const existing = chosen
    ? sources.find((option) => (option.supplier?.id ?? null) === (chosen.supplier?.id ?? null) && !option.buying_group)
    : undefined
  const chosenName = chosen ? (chosen.supplier?.name ?? po.manufacturer?.short_name ?? 'the manufacturer') : null
  const unchanged = existing !== undefined && existing.id === po.source?.sourcing_option_id
  const pending = createSource.isPending || updatePO.isPending || switchSource.isPending
  const fieldId = `switch-supplier-${po.uuid}`

  const confirm = async (close: () => void) => {
    if (!chosen) return
    let switching = false
    try {
      // The first source becomes the preferred one, so a manufacturer with none records direct first.
      if (!existing && sources.length === 0 && chosen.supplier !== null) {
        await createSource.mutateAsync({ supplier_id: null })
      }
      const sourcingOptionId =
        existing?.id ?? (await createSource.mutateAsync({ supplier_id: chosen.supplier?.id ?? null })).id
      if (!sent) {
        await updatePO.mutateAsync({ sourcing_option_id: sourcingOptionId })
        close()
        onClose()
        return
      }
      switching = true
      const result = await switchSource.mutateAsync({ sourcing_option_id: sourcingOptionId })
      close()
      onClose()
      navigate({ to: '/purchase-orders/$poId', params: { poId: result.purchase_order.uuid } })
    } catch (error) {
      // The switch toasts its own failure; adding a source or an unsent change does not.
      if (!switching) toast.error(formatApiErrorMessage('Supplier not switched', error))
    }
  }

  return (
    <ActionPopover
      anchorOnly
      trigger={trigger}
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={sent ? 'Switch supplier' : 'Order through'}
      confirmLabel={chosenName ? `${sent ? 'Switch to' : 'Order through'} ${chosenName}` : 'Choose a supplier'}
      pending={pending}
      pendingLabel="Switching…"
      canConfirm={Boolean(chosen) && !unchanged && !pending}
      onConfirm={(_note, close) => void confirm(close)}
    >
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor={fieldId} className="text-xs text-muted-foreground">
            Order through
          </Label>
          <SupplierPicker
            id={fieldId}
            value={chosen ? (chosen.supplier?.id ?? null) : undefined}
            onChange={(supplier) => setPicked({ supplier })}
            disabled={pending}
          />
        </div>
        {sent && chosenName && !unchanged && (
          <p className="text-sm">
            A new PO goes to {chosenName}. {currentName ?? 'The current supplier'} is told PO {po.po_number} is
            cancelled.
          </p>
        )}
      </div>
    </ActionPopover>
  )
}
